import React, { useEffect, useMemo, useState } from "react";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const today = () => new Date().toISOString().slice(0, 10);
const money = (value) => `$${Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const RecordReceivedPaymentDialog = ({ open, onOpenChange, onSaved }) => {
  const [colonies, setColonies] = useState([]);
  const [colonyId, setColonyId] = useState("");
  const [amount, setAmount] = useState("");
  const [paidAt, setPaidAt] = useState(today());
  const [method, setMethod] = useState("Check");
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [plan, setPlan] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setColonyId(""); setAmount(""); setPaidAt(today()); setMethod("Check"); setReference(""); setNote(""); setPlan(null);
    api.get("/mgmt/colonies")
      .then(({ data }) => setColonies((data || []).filter((colony) => colony.active !== false)))
      .catch((error) => toast.error(formatErr(error.response?.data?.detail) || error.message));
  }, [open]);

  const payload = useMemo(() => ({
    colony_id: colonyId,
    amount: Number(amount || 0),
    paid_at: paidAt,
    payment_method: method,
    payment_reference: reference,
    note,
  }), [colonyId, amount, paidAt, method, reference, note]);

  const changeColony = (value) => { setColonyId(value); setPlan(null); };
  const changeAmount = (event) => { setAmount(event.target.value); setPlan(null); };

  const preview = async () => {
    if (!colonyId || Number(amount || 0) <= 0) return toast.error("Choose a colony and enter the payment amount first.");
    setPreviewing(true);
    try {
      const { data } = await api.post("/mgmt/payments/preview-allocation", payload);
      setPlan(data);
    } catch (error) { toast.error(formatErr(error.response?.data?.detail) || error.message); }
    finally { setPreviewing(false); }
  };

  const save = async () => {
    if (!plan) return toast.error("Prefill the invoice allocation first.");
    setSaving(true);
    try {
      const { data } = await api.post("/mgmt/payments/allocate", payload);
      toast.success(`Recorded ${money(data.amount)} across ${data.allocations.length} invoice${data.allocations.length === 1 ? "" : "s"}`);
      onSaved?.(data);
      onOpenChange(false);
    } catch (error) { toast.error(formatErr(error.response?.data?.detail) || error.message); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent closeTestId="received-payment-record-close" className="max-w-xl max-h-[90dvh] overflow-y-auto" data-testid="received-payment-record-dialog">
        <DialogHeader>
          <DialogTitle data-testid="received-payment-record-title">Record received payment</DialogTitle>
          <DialogDescription>Enter the payment, then prefill invoices. Exact balances match one invoice; otherwise the oldest unpaid invoices are filled first.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Colony</Label><Select value={colonyId} onValueChange={changeColony}><SelectTrigger data-testid="received-payment-record-colony"><SelectValue placeholder="Choose colony" /></SelectTrigger><SelectContent>{colonies.map((colony) => <SelectItem key={colony.id} value={colony.id}>{colony.name}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-1.5"><Label>Payment amount</Label><Input type="number" min="0.01" step="0.01" value={amount} onChange={changeAmount} placeholder="0.00" data-testid="received-payment-record-amount" /></div>
            <div className="space-y-1.5"><Label>Payment date</Label><Input type="date" value={paidAt} onChange={(event) => setPaidAt(event.target.value)} data-testid="received-payment-record-date" /></div>
            <div className="space-y-1.5"><Label>Method</Label><Select value={method} onValueChange={setMethod}><SelectTrigger data-testid="received-payment-record-method"><SelectValue /></SelectTrigger><SelectContent>{["Check", "Zelle", "ACH", "Bank transfer", "Cash", "Credit card", "Other"].map((item) => <SelectItem key={item} value={item}>{item}</SelectItem>)}</SelectContent></Select></div>
          </div>
          {/(check|zelle|ach|bank)/i.test(method) && <div className="space-y-1.5"><Label>Reference / check number</Label><Input value={reference} onChange={(event) => setReference(event.target.value)} data-testid="received-payment-record-reference" /></div>}
          <div className="space-y-1.5"><Label>Note</Label><Input value={note} onChange={(event) => setNote(event.target.value)} placeholder="Optional" data-testid="received-payment-record-note" /></div>
          <Button type="button" variant="outline" onClick={preview} disabled={previewing} className="w-full" data-testid="received-payment-prefill-button"><Sparkles className="w-4 h-4 mr-2" />{previewing ? "Prefilling…" : "Prefill invoices"}</Button>

          {plan && (
            <div className="border border-border rounded-sm divide-y divide-border" data-testid="received-payment-allocation-plan">
              <div className="p-3 bg-secondary/40 flex items-center justify-between gap-3 text-sm"><span>{plan.exact_match ? "Exact invoice match" : "Oldest unpaid invoices first"}</span><span className="font-semibold" data-testid="received-payment-total-unpaid">Total unpaid {money(plan.total_unpaid)}</span></div>
              {plan.allocations.map((allocation, index) => (
                <div key={allocation.invoice_id} className="p-3 flex items-center justify-between gap-3 text-sm" data-testid={`received-payment-allocation-${index}`}>
                  <div><div className="font-semibold">{allocation.invoice_number}</div><div className="text-xs text-muted-foreground">Due {money(allocation.balance_before)} → {money(allocation.balance_after)}</div></div>
                  <div className="font-semibold tabular-nums">{money(allocation.amount)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="received-payment-record-cancel">Cancel</Button>
          <Button type="button" onClick={save} disabled={!plan || saving} data-testid="received-payment-record-submit">{saving ? "Recording…" : "Record payment"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};